import BaseApiService from './BaseApiService.js';

class DepartmentService extends BaseApiService {

  // ✅ Create a new department
  async createDepartment(departmentData) {
    // Validate required fields
    if (!departmentData.departmentName || !departmentData.departmentName.trim()) {
      throw new Error('Department name is required');
    }
    if (!departmentData.departmentCode || !departmentData.departmentCode.trim()) {
      throw new Error('Department code is required');
    }

    // Clean up payload - remove orgId as backend extracts it from token
    const cleanedData = {
      departmentName: departmentData.departmentName.trim(),
      departmentCode: departmentData.departmentCode.trim(),
      status: departmentData.status || 'ACTIVE'
    };

    console.log('📤 Creating department with payload:', cleanedData);
    console.log('ℹ️ OrgId will be extracted from authentication token');
    
    try {
      const response = await this.makeRequest('/api/departments/create', {
        method: 'POST',
        body: JSON.stringify(cleanedData),
      });
      console.log('✅ Department created successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Failed to create department:', error);
      throw error;
    }
  }

  // ✅ Get all departments
  async getAllDepartments() {
    try {
      console.log('Fetching all departments...');
      const data = await this.makeRequest('/api/departments/all', {
        method: 'GET'
      });
      console.log('Departments received:', data);
      return data;
    } catch (error) {
      console.error('Failed to fetch departments:', error);
      throw error;
    }
  }

  // ✅ Get departments by organization
  async getDepartmentsByOrg(orgId) {
    if (!orgId) {
      console.warn('⚠️ OrgId is null or undefined, cannot fetch departments');
      return [];
    }
    try {
      console.log(`🚀 Fetching all departments and filtering by orgId: ${orgId} (Type: ${typeof orgId})`);
      // Fetch all departments and filter by orgId on frontend
      const response = await this.makeRequest('/api/departments/all', { 
        method: 'GET'
      });
      
      console.log('📥 Raw API response:', response);
      
      // Extract departments array from response
      let departmentsArray = [];
      if (Array.isArray(response)) {
        departmentsArray = response;
        console.log('✅ Response is direct array with', departmentsArray.length, 'departments');
      } else if (response && response.departments && Array.isArray(response.departments)) {
        departmentsArray = response.departments;
        console.log('✅ Response has departments property with', departmentsArray.length, 'departments');
      } else if (response && Array.isArray(response.data)) {
        departmentsArray = response.data;
        console.log('✅ Response has data property with', departmentsArray.length, 'departments');
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        return [];
      }
      
      // Filter departments by orgId (handle both string "GDC-01" and numeric orgIds)
      const filtered = departmentsArray.filter(department => {
        const deptOrgId = department.orgId;
        // Always use string comparison to handle "GDC-01" style orgIds
        const matches = deptOrgId != null && String(deptOrgId) === String(orgId);
        if (matches) {
          console.log(`✅ Department "${department.departmentName}" matches orgId ${orgId}`);
        }
        return matches;
      });
      
      console.log(`📊 Filtered ${filtered.length} departments for orgId: ${orgId}`);
      return filtered;
    } catch (error) {
      console.error(`❌ Failed to fetch departments for org ${orgId}:`, error);
      throw error;
    }
  }

  // ✅ Get department by ID
  async getDepartmentById(departmentId) {
    return this.makeRequest(`/api/departments/${departmentId}`);
  }

  // ✅ Get department by code
  async getDepartmentByCode(departmentCode) {
    return this.makeRequest(`/api/departments/code/${departmentCode}`);
  }

  // ✅ Update department
  async updateDepartment(departmentId, departmentData) {
    return this.makeRequest(`/api/departments/${departmentId}`, {
      method: 'PUT',
      body: JSON.stringify(departmentData),
    });
  }

  // ✅ Delete department
  async deleteDepartment(departmentId) {
    return this.makeRequest(`/api/departments/${departmentId}`, {
      method: 'DELETE',
    });
  }

  // ✅ Deactivate department
  async deactivateDepartment(departmentId) {
    return this.makeRequest(`/api/departments/${departmentId}/deactivate`, {
      method: 'PUT',
    });
  }

  // ✅ Activate department
  async activateDepartment(departmentId) {
    return this.makeRequest(`/api/departments/${departmentId}/activate`, {
      method: 'PUT',
    });
  }

  // ✅ Search departments by name
  async searchDepartmentsByName(departmentName) {
    return this.makeRequest(`/api/departments/search?departmentName=${encodeURIComponent(departmentName)}`);
  }

  // ✅ Get department count for organization
  async getDepartmentCount(orgId) {
    const numericOrgId = parseInt(orgId, 10);
    return this.makeRequest(`/api/departments/org/${numericOrgId}/count`);
  }

  // ✅ Get active departments
  async getActiveDepartments(orgId) {
    const numericOrgId = parseInt(orgId, 10);
    return this.makeRequest(`/api/departments/org/${numericOrgId}/active`);
  }

  // ✅ Get inactive departments
  async getInactiveDepartments(orgId) {
    const numericOrgId = parseInt(orgId, 10);
    return this.makeRequest(`/api/departments/org/${numericOrgId}/inactive`);
  }

  // ✅ Get active departments by alphanumeric string orgId (public/pre-registration)
  async getActiveDepartmentsByOrgIdStr(orgIdStr) {
    try {
      return this.makeRequest(`/api/departments/org/string/${orgIdStr}/active`, {
        method: 'GET'
      });
    } catch (error) {
      console.error(`Failed to fetch active departments for string orgId ${orgIdStr}:`, error);
      return [];
    }
  }
}

// ✅ Export single instance
const departmentService = new DepartmentService();
export default departmentService;
